import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from 'luxon';
import '../ClientBookings/DetailsComponents/clientBookingDetails.css';
import './bookingRequestDetails.css';
import {
    isPaymentInReview,
    paymentInReviewMessage,
    isPendingDrift,
    driftCaptureTotal,
    pendingDriftMessage,
    paymentErrorMessage,
    readCancelSettlement,
    readCancelErrorSettlement,
    CANCEL_UNCONFIRMED_MESSAGE,
    isReviewHeldRow,
    REVIEW_HELD_NOTE,
    REVIEW_HELD_WITHDRAW_NOTE,
    REVIEW_HELD_OTHER_BOOKINGS_NOTE,
} from '../paymentErrors';
import { clientFundingRows } from './requestFundingLegs';

// Pending create_booking request — rendered as a PROPER booking details page
// (2026-07-17). Same layout, sections and styling as ClientBookingDetails (cbd__*
// classes): property header + status chip, info grid, tasks, fees/quote,
// notes. Read-only — no edit/cancel-booking actions — with the request
// actions integrated where a real booking's action buttons sit (top-right):
//   · payment_state === 'awaiting_payment' (card checkout still 'created'):
//     amber chip + "Complete Payment" (resume the paypage) and "Cancel
//     Payment" (POST /api/payments/cancel — releases the staged request).
//   · awaiting review: "Withdraw Request" with an inline confirm step and a
//     PERSISTENT confirmation that quotes released_to_wallet ("AED X from
//     your payment stays in your wallet") — no toasts, no timers.
//
// The request is re-fetched on open (by id from /requests/my) so the page
// always shows the current state, not a stale list row.

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtWhen = (bookingStart) => {
    const dt = DateTime.fromISO(String(bookingStart || '').replace(' ', 'T'), { zone: 'Asia/Dubai' });
    return dt.isValid ? dt.toFormat('dd LLL yyyy, HH:mm') : '—';
};

export default function BookingRequestDetails() {
    const { requestId } = useParams();
    const navigate = useNavigate();

    const [request, setRequest] = useState(null);
    const [terminal, setTerminal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    // phase: 'view' | 'confirmWithdraw' | 'confirmCancelPay' | 'withdrawn'
    //      | 'payCancelled'   — cancel VERIFIED: nothing charged, request dropped
    //      | 'payCompleted'   — the cancel discovered the payment had actually
    //                           gone through (lost redirect); the request IS
    //                           submitted and the client WAS charged
    const [phase, setPhase] = useState('view');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // Calm, non-error notices about money (amount changed / payment in review).
    const [notice, setNotice] = useState('');
    // Fresh figure quoted by a PENDING_DRIFT response and SHOWN to the client —
    // resent as expected_total so the next attempt is an acknowledged amount.
    const [ackTotal, setAckTotal] = useState(null);
    const [releasedToWallet, setReleasedToWallet] = useState(null);

    // Refetch-on-open: pull the request fresh by id (ALL statuses, not just
    // pending) so a just-approved request doesn't keep rendering as pending on
    // reload. An approved create_booking now sends the client straight to the
    // booking it became — the sensible landing (2026-07-20).
    const fetchRequest = useCallback(async () => {
        try {
            const res = await axios.get('/api/client-requests/requests/my');
            const rows = Array.isArray(res.data?.requests) ? res.data.requests : [];
            const match = rows.find(
                (r) => String(r.id) === String(requestId)
                    && r.request_type === 'create_booking'
            );
            if (!match) {
                setNotFound(true);
                return;
            }
            // Approved → it's a real booking now: redirect to that booking page.
            if (match.status === 'approved' && match.result_id) {
                navigate(`/client/bookings/${match.result_id}`, { replace: true });
                return;
            }
            // 'awaiting_payment' is LIVE, not terminal: the request is staged and
            // its card checkout is still open — the client must see the amber
            // "Awaiting payment" state with Complete/Cancel Payment, never a
            // "Request cancelled" banner (2026-08-06, REQ-5903).
            if (match.status === 'pending' || match.status === 'awaiting_payment') {
                setRequest(match);
                return;
            }
            // TERMINAL (rejected / cancelled / withdrawn). These used to be
            // thrown away with setNotFound(true), so the client hit a generic
            // "no longer pending" dead-end that never said it was REJECTED,
            // never gave the reason, and never said where their payment went —
            // even though all of it is right here on the row. Keep it and
            // render the truth (2026-07-23).
            setRequest(match);
            setTerminal(match);
        } catch (err) {
            if (err.response?.status !== 503) console.error('Error fetching booking request:', err);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    }, [requestId, navigate]);

    useEffect(() => {
        fetchRequest();
    }, [fetchRequest]);

    if (loading) return (
        <div className="app-loading">
            <div className="app-spinner" />
            <div className="app-spinner-text">Loading Booking Details...</div>
        </div>
    );

    // Persistent outcome states — the page stays on these until the client
    // navigates away themselves (no toasts, no timers).
    if (phase === 'withdrawn' || phase === 'payCancelled' || phase === 'payCompleted') {
        const withdrawn = phase === 'withdrawn';
        const completed = phase === 'payCompleted';
        return (
            <div className="cbd">
                <div className="cbd__header">
                    <Link to="/client/bookings" className="cbd__back">← Back to Bookings</Link>
                </div>
                <div className="cbd__card cbrq-done">
                    <div className={`cbrq-doneIcon ${withdrawn || completed ? 'cbrq-doneIcon--ok' : 'cbrq-doneIcon--neutral'}`} aria-hidden="true">✓</div>
                    <h3 className="cbrq-doneTitle">
                        {withdrawn ? 'Request withdrawn' : (completed ? 'Your payment actually completed' : 'Payment cancelled')}
                    </h3>
                    <p className="cbrq-doneText">
                        {withdrawn && (
                            <>
                                Your booking request has been withdrawn — nothing will be scheduled.
                                {releasedToWallet != null && (
                                    <> <strong>{money(releasedToWallet)} from your payment stays in your wallet</strong> and
                                        can back your next booking or be withdrawn anytime.</>
                                )}
                            </>
                        )}
                        {/* Truthful correction (the owner, money-copy rule): the cancel
                            attempt found the payment had ALREADY gone through —
                            we must never tell a charged client nothing was taken. */}
                        {completed && (
                            <>
                                This payment couldn’t be cancelled because it had already gone through.
                                <strong> Your payment was received and this booking request has been submitted
                                for review.</strong> There is nothing more to pay — please don’t request this
                                booking again. We’ll notify you the moment our team confirms it.
                            </>
                        )}
                        {!withdrawn && !completed && (
                            <>
                                The pending card payment has been cancelled — nothing was charged and this booking
                                request will not be submitted. You can request the booking again anytime.
                            </>
                        )}
                    </p>
                    <button type="button" className="cbd__requestBtn" onClick={() => navigate(completed ? '/client/home' : '/client/bookings')}>
                        {completed ? 'View My Requests' : 'Back to Bookings'}
                    </button>
                </div>
            </div>
        );
    }

    if (notFound || !request) {
        return (
            <div className="cbd">
                <div className="cbd__header">
                    <Link to="/client/bookings" className="cbd__back">← Back to Bookings</Link>
                </div>
                <div className="cbd__card cbrq-done">
                    <h3 className="cbrq-doneTitle">This request is no longer pending</h3>
                    <p className="cbrq-doneText">
                        It may have just been approved by our team — in which case it now appears as a
                        booking — or it was withdrawn. Check your bookings or your requests for the
                        latest status.
                    </p>
                    <div className="cbrq-doneActions">
                        <button type="button" className="cbd__requestBtn" onClick={() => navigate('/client/bookings')}>
                            View Bookings
                        </button>
                        <button type="button" className="cbd__requestBtn cbrq-btnGhost" onClick={() => navigate('/client/home')}>
                            My Requests
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const p = request.payload || {};
    // The bank is holding an authorised payment for THIS request. There is no
    // live paypage to resume (the server nulls payment_url once it classifies
    // the hold) and withdrawing would mean guessing about the client's money —
    // so this state offers no actions at all, only a standing explanation.
    const reviewHeld = isReviewHeldRow(request);
    // A terminal request NEVER wears the awaiting-payment state, even if its
    // last checkout row still reads 'created' — Cancelled and "Awaiting
    // payment" on the same screen is a contradiction (2026-08-06).
    const awaitingPayment = !terminal && !reviewHeld && request.payment_state === 'awaiting_payment';
    const estimate = p.estimate || null;
    const total = estimate?.total ?? request.estimated_total ?? p.estimated_total ?? null;
    const tasks = Array.isArray(p.tasks) ? p.tasks.filter((t) => (t.instruction || '').trim()) : [];
    const propertyLabel = p.property_label || (p.property_id ? `Property #${p.property_id}` : '—');
    // A6 — the server's funding_legs (same list the admin review renders),
    // client-phrased. Empty ⇒ the Payment card is not rendered at all.
    const fundingRows = clientFundingRows(request);

    // Resume the interrupted card checkout — ALWAYS mint a fresh paypage via
    // /payments/retry. The stored payment_url points at an N-Genius paypage
    // session that expires server-side; reopening it minutes later lands on a
    // dead gateway page, which is why "Complete payment" never seemed to work
    // (2026-08-06). /retry supersedes the old checkout atomically and
    // returns a live URL, so this path is deterministic.
    const handleCompletePayment = async () => {
        if (busy) return;
        setError('');
        // A drift notice is only cleared once the client has acted on it — but a
        // fresh attempt starts from a clean slate otherwise.
        if (ackTotal == null) setNotice('');
        // No checkout to retry — there is genuinely nothing to reopen. Say what
        // to do next instead of a dead "try again".
        if (!request.order_ref) {
            setNotice('We can’t reopen this payment page. Please refresh — if it still doesn’t open, '
                + 'withdraw this request and book again. Nothing is charged twice.');
            return;
        }
        setBusy(true);
        try {
            const body = { order_ref: request.order_ref };
            if (ackTotal != null) body.expected_total = ackTotal;
            const res = await axios.post('/api/payments/retry', body);
            if (res.data?.payment_url) {
                window.location.assign(res.data.payment_url);
                return; // navigating away
            }
            // 200 without a paypage URL — persistent notice, and truthful about
            // money: this attempt charged nothing.
            setNotice('We couldn’t open the secure payment page just now. Please try again in a moment — '
                + 'nothing has been charged for this attempt.');
            setBusy(false);
        } catch (err) {
            // The amount due moved — quote the fresh figure, remember it, and let
            // the next tap pay the acknowledged amount instead of dead-ending.
            if (isPendingDrift(err)) {
                setAckTotal(driftCaptureTotal(err));
                setNotice(pendingDriftMessage(err));
            } else if (isPaymentInReview(err)) {
                setNotice(paymentInReviewMessage(err));
            } else {
                setError(paymentErrorMessage(err, 'Could not open the payment page. Please try again.'));
            }
            setBusy(false);
        }
    };

    const handleCancelPayment = async () => {
        if (busy || !request.order_ref) return;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            const res = await axios.post('/api/payments/cancel', { order_ref: request.order_ref });
            // /cancel returns the checkout's RESULTING outcome. 'paid' means the
            // client had in fact completed the payment (lost redirect) and the
            // cancel attempt settled it: they WERE charged and the request WAS
            // submitted. Showing "nothing was charged" here would be a lie about
            // money that invites a duplicate booking and a bank dispute.
            // ONE reader decides (shared with mobile). Fail CLOSED: only an
            // explicit cancelled/failed/expired may show the "nothing was
            // charged" confirmation. A missing, renamed or unknown outcome
            // leaves the request visible and tells the client we are still
            // confirming — never that they weren't charged.
            const settlement = readCancelSettlement(res);
            if (settlement === 'paid') {
                setPhase('payCompleted');
                fetchRequest();
                return;
            }
            if (settlement === 'cancelled') {
                setPhase('payCancelled');
                return;
            }
            setPhase('view');
            setNotice(CANCEL_UNCONFIRMED_MESSAGE);
            fetchRequest();
        } catch (err) {
            const settlement = readCancelErrorSettlement(err);
            if (settlement === 'review') {
                // Gateway still holding the money and the void wasn't verifiable
                // — calm, persistent information, not a red failure.
                setPhase('view');
                setNotice(paymentInReviewMessage(err));
                fetchRequest();
            } else if (settlement === 'paid') {
                setPhase('payCompleted');
                fetchRequest();
            } else if (settlement === 'cancelled') {
                setPhase('payCancelled');
            } else if (err.response) {
                // Ambiguous server answer (bare 409/503, proxy, unseen backend
                // code) — do not claim anything about the money.
                setPhase('view');
                setNotice(CANCEL_UNCONFIRMED_MESSAGE);
                fetchRequest();
            } else {
                setError(paymentErrorMessage(err, 'Could not cancel the payment. Please try again.'));
            }
        } finally {
            setBusy(false);
        }
    };

    const handleWithdraw = async () => {
        if (busy) return;
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(`/api/client-requests/requests/${request.id}/cancel`);
            const released = Number(res.data?.released_to_wallet);
            setReleasedToWallet(Number.isFinite(released) && released > 0.005 ? released : null);
            setPhase('withdrawn');
        } catch (err) {
            // Withdraw refuses while the gateway holds money for this request.
            if (isPaymentInReview(err)) {
                setPhase('view');
                setNotice(paymentInReviewMessage(err));
                fetchRequest();
            } else {
                setError(paymentErrorMessage(err, 'Could not withdraw this request. Please refresh and try again.'));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="cbd">
            {/* Header — same shape as the real booking details header */}
            <div className="cbd__header">
                <Link to="/client/bookings" className="cbd__back">← Back to Bookings</Link>
                <div className="cbd__headerMain">
                    <div>
                        <h1 className="cbd__title">
                            Requested Booking
                            <span className="creq-req-id" style={{ marginLeft: 8, verticalAlign: 'middle' }} title="Request reference">REQ-{request.id}</span>
                        </h1>
                        <div className="cbd__subtitle">{propertyLabel}</div>
                        <div className="cbd__statusRow">
                            {terminal ? (
                                <span className="cbd__statusChip cbd__statusChip--danger">
                                    {terminal.status === 'rejected' ? 'Declined' : 'Cancelled'}
                                </span>
                            ) : reviewHeld ? (
                                <span className="cbd__statusChip cbrq-statusChip--review" title="The bank is reviewing this card payment">
                                    Payment in review
                                </span>
                            ) : awaitingPayment ? (
                                <span className="cbd__statusChip cbrq-statusChip--payment" title="Not sent to our team yet — submitted as soon as the card payment is completed">
                                    Not submitted — awaiting payment
                                </span>
                            ) : (
                                <span className="cbd__statusChip cbd__statusChip--orange">Pending review</span>
                            )}
                            <span className="cbrq-requestedAt">
                                Requested {request.created_at
                                    ? DateTime.fromISO(request.created_at).setZone('Asia/Dubai').toFormat('d LLL yyyy, HH:mm')
                                    : '—'}
                            </span>
                        </div>
                    </div>

                    {/* Top-right: request actions — sitting exactly where a real
                        booking's Edit/Cancel Booking buttons sit. */}
                    <div className="cbd__headerRight">
                        <div className="cbd__requestActions">
                            {/* Review-held → NO actions. Not a disabled button
                                either: a greyed "Withdraw" reads as a thing the
                                client failed to do. The panel below explains. */}
                            {terminal ? null : reviewHeld ? null : awaitingPayment ? (
                                <>
                                    <button
                                        type="button"
                                        className="cbd__requestBtn cbd__requestBtn--danger"
                                        onClick={() => { setError(''); setPhase('confirmCancelPay'); }}
                                        disabled={busy}
                                    >
                                        Cancel Payment
                                    </button>
                                    <button
                                        type="button"
                                        className="cbd__requestBtn"
                                        onClick={handleCompletePayment}
                                        disabled={busy}
                                    >
                                        {busy
                                            ? 'Opening secure page…'
                                            : (ackTotal != null ? 'Pay Updated Amount' : 'Complete Payment')}
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="cbd__requestBtn cbd__requestBtn--danger"
                                    onClick={() => { setError(''); setPhase('confirmWithdraw'); }}
                                    disabled={busy}
                                >
                                    Withdraw Request
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Amber state panel — same visual language as the pending-change
                    panel on a real booking's page. */}
                {/* Amber = waiting, blue = in review, RED = dead. A declined
                    request wearing the same amber as a pending one reads as
                    "still going" at a glance (2026-07-23). */}
                <div className={`cbd__pendingPanel${terminal ? ' cbrq-panel--terminal' : reviewHeld ? ' cbrq-panel--review' : ''}`}>
                    <div className="cbd__pendingPanelHead">
                        <span className="cbd__requestChip">
                            {terminal
                                ? (terminal.status === 'rejected' ? 'Request declined' : 'Request cancelled')
                                : reviewHeld ? 'Payment in review' : (awaitingPayment ? 'Not submitted — awaiting payment' : 'Pending review')}
                        </span>
                        <span className="cbd__pendingPanelSub">
                            {terminal
                                ? (terminal.review_notes
                                    || (terminal.status === 'rejected'
                                        ? 'Our team could not accept this request, so it will not be scheduled.'
                                        : 'This request was cancelled and will not be scheduled.'))
                                : reviewHeld
                                ? REVIEW_HELD_NOTE
                                : (awaitingPayment
                                    ? (
                                        <>
                                            This request has <strong>not been submitted to our team yet</strong> — it is
                                            sent for review the moment the card payment is completed.
                                            {Number(request.payment_capture_total) > 0.009 && (
                                                <> Amount to pay: <strong>AED {Number(request.payment_capture_total).toFixed(2)}</strong>
                                                {Number(request.payment_capture_total) - (Number(total) || 0) > 0.01
                                                    ? ' (includes your pending invoices).' : '.'}</>
                                            )}
                                            {' '}Complete the payment to submit it, or cancel it.
                                        </>
                                    )
                                    : 'Awaiting our team’s review — nothing is confirmed yet. This is exactly what you requested.')}
                        </span>
                    </div>

                    {/* Booking notices for a request still awaiting review —
                        server-decided, wording included
                        (routes/services/bookingNotice.js). */}
                    {!terminal && !reviewHeld
                        && (request.booking_notice?.last_minute_note || request.booking_notice?.office_hours_note) && (
                        <div className="cbrq-bookingNotice" role="note">
                            {request.booking_notice.last_minute_note && (
                                <div className="cbrq-bookingNotice__strong">{request.booking_notice.last_minute_note}</div>
                            )}
                            {request.booking_notice.office_hours_note && (
                                <div>{request.booking_notice.office_hours_note}</div>
                            )}
                        </div>
                    )}

                    {/* Why there is no Withdraw button, and the reassurance that
                        matters most: everything else still works. */}
                    {reviewHeld && (
                        <div className="cbrq-reviewNotes">
                            <div>{REVIEW_HELD_WITHDRAW_NOTE}</div>
                            <div className="cbrq-reviewNotes__strong">{REVIEW_HELD_OTHER_BOOKINGS_NOTE}</div>
                            <button
                                type="button"
                                className="cbd__pendingWithdraw"
                                onClick={() => { setNotice(''); fetchRequest(); }}
                                disabled={busy}
                            >
                                Check status
                            </button>
                        </div>
                    )}

                    {phase === 'confirmWithdraw' && !reviewHeld && (
                        <div className="cbrq-confirm">
                            <div className="cbrq-confirmText">
                                Withdraw this booking request? Nothing will be scheduled
                                {awaitingPayment ? '' : ' and any amount already paid stays in your wallet'}.
                            </div>
                            <div className="cbrq-confirmActions">
                                <button type="button" className="cbd__pendingWithdraw" onClick={() => setPhase('view')} disabled={busy}>
                                    Keep request
                                </button>
                                <button type="button" className="cbd__requestBtn cbd__requestBtn--danger" onClick={handleWithdraw} disabled={busy}>
                                    {busy ? 'Withdrawing…' : 'Withdraw request'}
                                </button>
                            </div>
                        </div>
                    )}

                    {phase === 'confirmCancelPay' && (
                        <div className="cbrq-confirm">
                            {/* Conditional by design: whether anything was charged
                                is only known AFTER the server checks the gateway.
                                The definitive wording lives in the post-cancel
                                confirmation, which follows a verified outcome. */}
                            <div className="cbrq-confirmText">
                                Cancel this payment? If it hasn’t been completed, nothing will be charged and this
                                booking request will not be submitted.
                            </div>
                            <div className="cbrq-confirmActions">
                                <button type="button" className="cbd__pendingWithdraw" onClick={() => setPhase('view')} disabled={busy}>
                                    Keep payment
                                </button>
                                <button type="button" className="cbd__requestBtn cbd__requestBtn--danger" onClick={handleCancelPayment} disabled={busy}>
                                    {busy ? 'Cancelling…' : 'Cancel this payment'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Calm money notice (amount changed / payment under gateway
                    review) — persistent, and deliberately not the red error. */}
                {!!notice && <div className="cbrq-notice">{notice}</div>}
                {!!error && <div className="cbrq-error">{error}</div>}
            </div>

            {/* Info grid — mirrors the real booking's card exactly */}
            <div className="cbd__card">
                <div className="cbd__infoGrid">
                    <div className="cbd__infoItem">
                        <div className="cbd__infoLabel">Date & Time</div>
                        <div className="cbd__infoValue">{fmtWhen(p.booking_start)}</div>
                    </div>
                    <div className="cbd__infoItem">
                        <div className="cbd__infoLabel">Duration</div>
                        <div className="cbd__infoValue">{p.duration_hours != null ? `${p.duration_hours} hours` : '—'}</div>
                    </div>
                    <div className="cbd__infoItem">
                        <div className="cbd__infoLabel">Type</div>
                        <div className="cbd__infoValue">{p.type_label || '—'}</div>
                    </div>
                    {p.required_cleaners != null && Number(p.required_cleaners) > 1 && (
                        <div className="cbd__infoItem">
                            <div className="cbd__infoLabel">Cleaners</div>
                            <div className="cbd__infoValue">{p.required_cleaners}</div>
                        </div>
                    )}
                    <div className="cbd__infoItem">
                        <div className="cbd__infoLabel">Amount (incl. VAT)</div>
                        <div className="cbd__infoValue cbd__infoValue--accent">
                            {total != null ? money(total) : '—'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tasks — the tasks the client asked for on this cleaning */}
            <div className="cbd__card">
                <h3 className="cbd__cardTitle">Tasks</h3>
                {tasks.length > 0 ? (
                    <ul className="cbrq-tasks">
                        {tasks.map((t, i) => (
                            <li className="cbrq-task" key={i}>
                                <span className="cbrq-taskNum">{i + 1}</span>
                                <span className="cbrq-taskText">
                                    {t.instruction}
                                    {t.is_report_required ? <span className="cbrq-taskFlag"> · photo report</span> : null}
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="cbrq-emptyLine">No tasks were added to this request.</div>
                )}
            </div>

            {/* Booking Fees — the quote the client saw when requesting */}
            <div className="cbd__card">
                <h3 className="cbd__cardTitle">Booking Fees</h3>
                {(estimate || total != null) ? (
                    <div className="cbrq-quote">
                        {Array.isArray(estimate?.line_items) && estimate.line_items.map((li, idx) => (
                            <div className="cbrq-quoteRow" key={idx}>
                                <span>{li.quantity > 1 ? `${li.quantity} × ` : ''}{li.name}</span>
                                <span>{money(li.line_subtotal)}</span>
                            </div>
                        ))}
                        {estimate?.subtotal != null && (
                            <div className="cbrq-quoteRow">
                                <span>Subtotal (excl. VAT)</span>
                                <span>{money(estimate.subtotal)}</span>
                            </div>
                        )}
                        {estimate?.vat != null && (
                            <div className="cbrq-quoteRow">
                                <span>VAT (5%)</span>
                                <span>{money(estimate.vat)}</span>
                            </div>
                        )}
                        {total != null && (
                            <div className="cbrq-quoteRow cbrq-quoteRow--total">
                                <span>Total incl. VAT</span>
                                <span>{money(total)}</span>
                            </div>
                        )}
                        <div className="cbrq-quoteNote">Final price confirmed on approval.</div>
                    </div>
                ) : (
                    <div className="cbrq-emptyLine">The quote will be confirmed on approval.</div>
                )}
            </div>

            {/* A6 — Payment: how this request is paid, straight from the
                server's funding legs (the same list our team sees at review),
                phrased for the client. One row per leg, gross figures, Dubai
                dates. Not rendered when the server sent nothing — we never
                guess a payment story. */}
            {fundingRows.length > 0 && (
                <div className="cbd__card">
                    <h3 className="cbd__cardTitle">Payment</h3>
                    <div className="cbrq-payLegs">
                        {fundingRows.map((r) => (
                            <div className={`cbrq-payLeg${r.credit ? ' cbrq-payLeg--credit' : ''}`} key={r.key}>
                                <div className="cbrq-payLegMain">
                                    <span className="cbrq-payLegLabel">{r.label}</span>
                                    {r.sub && <span className="cbrq-payLegSub">{r.sub}</span>}
                                </div>
                                {r.amount != null && <span className="cbrq-payLegAmount">{r.amount}</span>}
                            </div>
                        ))}
                    </div>

                    {/* MONEY WE STILL OWE. Only when the receipt ledger itself
                        says a captured card payment on a dead request has NOT
                        been returned — never inferred from a missing row, so a
                        failed read can't invent a debt or hide one. */}
                    {terminal && request.card_outcome?.state === 'outstanding' && (
                        <div className="cbrq-payOwed">
                            This payment has not been returned yet. Our team is on it — please
                            contact us if you don’t see it in your wallet shortly.
                        </div>
                    )}
                </div>
            )}

            {/* Notes (at the very bottom — same as the real booking page) */}
            {p.notes && (
                <div className="cbd__card">
                    <h3 className="cbd__cardTitle">Notes</h3>
                    <div className="cbd__notesBlock">
                        <div className="cbd__notesLabel">Booking Notes</div>
                        <div className="cbd__notesText">{p.notes}</div>
                    </div>
                </div>
            )}
        </div>
    );
}
